// Pure rules for moving an app to another domain. Structural parameter types
// so tests can pass plain objects; `@/…` imports are type-only.
import { friendlyApiError, type ErrorMessages } from './apiErrors.ts';
import { isRunningApp } from './appStatus.ts';
import type { DomainSwitchStatus } from '@/types/domain';

/** The happy path, in order; `failed` can end any of the first two. */
export const SWITCH_STEPS: readonly DomainSwitchStatus[] = ['in_progress', 'verifying', 'awaiting_cleanup', 'done'];

export const SWITCH_STEP_LABELS: Record<DomainSwitchStatus, string> = {
  in_progress: 'Adding new hostname',
  verifying: 'Verifying',
  awaiting_cleanup: 'Grace period',
  done: 'Done',
  failed: 'Failed',
};

/** Position in SWITCH_STEPS; -1 for failed. */
export function switchStepIndex(status: DomainSwitchStatus): number {
  return SWITCH_STEPS.indexOf(status);
}

type WithStatus = { status: DomainSwitchStatus } | null | undefined;

/** Not finished: a new switch is refused until it is. */
export function isSwitchOpen(sw: WithStatus): boolean {
  return sw != null && (sw.status === 'in_progress' || sw.status === 'verifying' || sw.status === 'awaiting_cleanup');
}

/** Both hostnames routed and the switch owns the app: other app operations return APP_BUSY. */
export function isSwitchOverlap(sw: WithStatus): boolean {
  return sw != null && (sw.status === 'in_progress' || sw.status === 'verifying');
}

export type ChangeDomainCheck = { ok: true } | { ok: false; reason: string };

/** Whether "Change domain" can be offered, and why not. `app.domain` is the domain's name ('' = none). */
export function canChangeDomain(
  app: { status: string; domain?: string | null; full_subdomain?: string | null },
  domains: readonly { name: string }[],
  latest: WithStatus,
): ChangeDomainCheck {
  if (isSwitchOverlap(latest)) return { ok: false, reason: 'A domain change is already in progress' };
  if (isSwitchOpen(latest)) return { ok: false, reason: 'The old hostname is still in its grace period' };
  if (!isRunningApp(app.status)) return { ok: false, reason: 'Start the app first' };
  if (!app.domain || !app.full_subdomain) return { ok: false, reason: 'This app has no managed hostname' };
  if (!domains.some((d) => d.name !== app.domain)) return { ok: false, reason: 'Add another domain in Settings first' };
  return { ok: true };
}

/** The app's hostname under another domain: its first label + the domain (as the server builds it). */
export function hostInDomain(host: string, domain: string): string {
  const i = host.indexOf('.');
  return `${i >= 0 ? host.slice(0, i) : host}.${domain}`;
}

export const GRACE_OPTIONS: readonly { minutes: number; label: string }[] = [
  { minutes: 0, label: 'Remove old hostname right away' },
  { minutes: 60, label: 'Keep old hostname for 1 hour' },
  { minutes: 1440, label: 'Keep old hostname for 1 day' },
  { minutes: 4320, label: 'Keep old hostname for 3 days' },
];

/** "1 hour", "3 days", "none". */
export function graceLabel(minutes: number): string {
  if (minutes <= 0) return 'none';
  if (minutes % 1440 === 0) return minutes === 1440 ? '1 day' : `${minutes / 1440} days`;
  if (minutes % 60 === 0) return minutes === 60 ? '1 hour' : `${minutes / 60} hours`;
  return `${minutes} minutes`;
}

const RECENT_MS = 24 * 60 * 60 * 1000;

/** Open switches, and finished ones for a day after they ended. */
export function showSwitchStatus(
  sw: { status: DomainSwitchStatus; completed_at: string | null; updated_at: string } | null | undefined,
  now: number,
): boolean {
  if (!sw) return false;
  if (isSwitchOpen(sw)) return true;
  const ended = Date.parse(sw.completed_at ?? sw.updated_at);
  return Number.isFinite(ended) && now - ended <= RECENT_MS;
}

const SWITCH_ERRORS: ErrorMessages = {
  APP_BUSY: 'The app is busy with another operation (deployment, migration or domain change). Try again when it has finished.',
  SAME_DOMAIN: 'The app is already on this domain.',
  APP_NOT_RUNNING: 'Start the app before changing its domain.',
  APP_NOT_ROUTABLE: 'This app has no managed hostname to move.',
  ORIGIN_TLS_NOT_READY:
    "The app's endpoint doesn't serve that domain's origin certificate yet. Push it from Settings → Domains, or choose another domain.",
  DOMAIN_NOT_FOUND: 'That domain no longer exists.',
  DOMAIN_REQUIRED: 'Choose a domain.',
  SUBDOMAIN_TAKEN: "The app's custom subdomain is already used or reserved in that domain. Choose another domain.",
  SUBDOMAIN_CHECK_FAILED: 'Could not check the subdomain in that domain. Try again.',
};

/** User-facing message for an error from api.apps.switchDomain. */
export function switchErrorMessage(err: unknown): string {
  return friendlyApiError(err, SWITCH_ERRORS, 'Failed to change the domain');
}
